import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { IonContent, IonPage } from "@ionic/react";
import GoogleMapReact from "google-map-react";
import { CircularProgress, MenuItem, TextField } from "@mui/material";
import { ArrowLeft, MapPin, Navigation } from "lucide-react";
import navigoServices from "../../../services/NavigoServices";
import { getProvidersByFilter, setStateFromPayload } from "../navigoSlice";
import Pin from "../../../components/common/location/Pin";

const RADIUS_PRESETS_KM = [6, 10, 15];
const CUSTOM_RANGE_STOPS_KM = [6, 10, 15, 30, 50, 75, 100];

const FIELD_SX = {
  "& .MuiInputBase-input": {
    fontFamily: "'Montserrat', sans-serif",
    color: "#12194C",
  },
  "& .MuiInputBase-root": { fontFamily: "'Montserrat', sans-serif" },
  "& .MuiInputLabel-root": {
    fontFamily: "'Montserrat', sans-serif",
    color: "#9CA3AF",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "#374151" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#374151" },
  "& .MuiFormLabel-asterisk, & .MuiInputLabel-asterisk": { color: "#DC2626" },
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Montserrat', sans-serif",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#D1D5DB" },
    "&.Mui-focused fieldset": { borderColor: "#9CA3AF" },
  },
};

export default function CentreFilters() {
  const dispatch = useDispatch();
  const history = useHistory();
  const routeLocation = useLocation();
  const service = routeLocation?.state?.service;
  const pageType = routeLocation?.state?.pageType;
  // Opened from the maps "Select manually" (no providers) — start on the
  // manual tab and route to the provider list on apply (not back to maps).
  const initialMode = routeLocation?.state?.initialMode;
  const fromMaps = routeLocation?.state?.fromMaps === true;

  const patientProfile = useSelector(
    (store) => store.login.user.patientProfile,
  );
  const navigoStore = useSelector((store) => store.navigo);
  const pickedLocation = navigoStore.ui?.pickedLocation;
  const centreFilter = navigoStore.ui?.centreFilter;

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const serviceMode = service?.serviceModeDetails?.[0]?.modeName;
  const serviceId = service?.serviceId;
  const planId = service?.planId;

  const initialRadiusKm =
    centreFilter?.mode === "maps"
      ? centreFilter.radiusKm
      : pickedLocation?.radiusKm || 6;

  const [mode, setMode] = useState(
    initialMode === "manual" || centreFilter?.mode === "manual"
      ? "manual"
      : "maps",
  );
  const [selectedRadiusKm, setSelectedRadiusKm] = useState(initialRadiusKm);
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [pendingCustomKm, setPendingCustomKm] = useState(
    !RADIUS_PRESETS_KM.includes(initialRadiusKm) ? initialRadiusKm : 30,
  );
  const isCustomActive = !RADIUS_PRESETS_KM.includes(selectedRadiusKm);

  const [manualState, setManualState] = useState(
    centreFilter?.mode === "manual" ? centreFilter.state || "" : "",
  );
  const [manualCity, setManualCity] = useState(
    centreFilter?.mode === "manual" ? centreFilter.city || "" : "",
  );
  const [manualLocality, setManualLocality] = useState(
    centreFilter?.mode === "manual" ? centreFilter.locality || "" : "",
  );

  const [states, setStates] = useState(
    navigoStore.ui?.locationFilterStates || [],
  );
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingLocalities, setLoadingLocalities] = useState(false);
  const [applying, setApplying] = useState(false);

  const fetchFilters = (type, body = {}) =>
    navigoServices.getLocationFilters({
      type,
      state: body.state || "",
      city: body.city || "",
      locality: body.locality || "",
      serviceMode,
      serviceId,
      planId,
    });

  useEffect(() => {
    if (states.length > 0) return undefined;
    let cancelled = false;
    setLoadingStates(true);
    fetchFilters("state")
      .then((res) => {
        if (!cancelled) setStates(res?.data?.state || []);
      })
      .catch(() => {
        if (!cancelled) setStates([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingStates(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload cities/localities so the prefilled (previously applied) manual
  // filter shows its selected options. Cleared across sessions when the maps
  // flow is freshly re-entered (centreFilter is reset there).
  useEffect(() => {
    if (centreFilter?.mode === "manual" && centreFilter.state) {
      setLoadingCities(true);
      fetchFilters("city", { state: centreFilter.state })
        .then((res) => setCities(res?.data?.city || []))
        .catch(() => setCities([]))
        .finally(() => setLoadingCities(false));
      if (centreFilter.city) {
        setLoadingLocalities(true);
        fetchFilters("locality", {
          state: centreFilter.state,
          city: centreFilter.city,
        })
          .then((res) => setLocalities(res?.data?.locality || []))
          .catch(() => setLocalities([]))
          .finally(() => setLoadingLocalities(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStateChange = (value) => {
    setManualState(value);
    setManualCity("");
    setManualLocality("");
    setCities([]);
    setLocalities([]);
    if (!value) return;
    setLoadingCities(true);
    fetchFilters("city", { state: value })
      .then((res) => setCities(res?.data?.city || []))
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  };

  const handleCityChange = (value) => {
    setManualCity(value);
    setManualLocality("");
    setLocalities([]);
    if (!value) return;
    setLoadingLocalities(true);
    fetchFilters("locality", { state: manualState, city: value })
      .then((res) => setLocalities(res?.data?.locality || []))
      .catch(() => setLocalities([]))
      .finally(() => setLoadingLocalities(false));
  };

  const handleSelectRadius = (km) => {
    setSelectedRadiusKm(km);
  };

  const openCustomRange = () => {
    setPendingCustomKm(isCustomActive ? selectedRadiusKm : 30);
    setCustomRangeOpen(true);
  };

  const applyCustomRange = () => {
    setCustomRangeOpen(false);
    setSelectedRadiusKm(pendingCustomKm);
  };

  // When the active filter is manual (or no map pick exists), the map preview
  // is stale — prompt the user to search a location on the map instead.
  const showMapSearchPrompt =
    centreFilter?.mode === "manual" || !pickedLocation;

  const goToMapSearch = () => {
    // Force the maps screen to do a fresh load even if Ionic re-enters the
    // cached /select-location view as a POP.
    dispatch(setStateFromPayload({ forceMapReset: true }));
    history.push({
      pathname: "/select-location",
      state: { service, pageType, planId },
    });
  };

  const isMapsChanged =
    mode === "maps" &&
    !showMapSearchPrompt &&
    (selectedRadiusKm !== initialRadiusKm || centreFilter?.mode === "manual");
  const isManualChanged =
    mode === "manual" &&
    Boolean(manualState) &&
    Boolean(manualCity) &&
    !(
      centreFilter?.mode === "manual" &&
      centreFilter.state === manualState &&
      centreFilter.city === manualCity &&
      (centreFilter.locality || "") === (manualLocality || "")
    );
  const canApply = (isMapsChanged || isManualChanged) && !applying;

  const handleApply = async () => {
    if (!canApply) return;
    setApplying(true);

    const base = {
      pageNum: 0,
      pageSize: 0,
      planId,
      serviceId,
      serviceMode,
      patientId: patientProfile?.id,
      memberPatientId: 0,
      languageMatch: [],
      serviceProviderIds: [],
      vendorDoctorId: 0,
      vendorCode: "",
      vendorId: "",
      providerAddressId: "",
    };

    let body;
    let newFilter;
    if (mode === "maps") {
      body = {
        ...base,
        locationFilter: null,
        geolocationFilter: {
          latitude: pickedLocation?.lat,
          longitude: pickedLocation?.lng,
          radius: selectedRadiusKm * 1000,
        },
      };
      newFilter = { mode: "maps", radiusKm: selectedRadiusKm };
    } else {
      body = {
        ...base,
        locationFilter: [
          {
            stateMatch: manualState,
            cityMatch: manualCity,
            landmarkMatch: manualLocality || "",
          },
        ],
        geolocationFilter: null,
      };
      newFilter = {
        mode: "manual",
        state: manualState,
        city: manualCity,
        locality: manualLocality,
      };
    }

    try {
      await dispatch(getProvidersByFilter(body)).unwrap();
      const uiUpdate = { centreFilter: newFilter };
      if (mode === "maps" && pickedLocation) {
        uiUpdate.pickedLocation = {
          ...pickedLocation,
          radiusKm: selectedRadiusKm,
        };
      }
      dispatch(setStateFromPayload(uiUpdate));
      if (fromMaps) {
        // Came from the maps "no providers → Select manually" path; there is
        // no provider list behind us yet, so go forward to it.
        history.replace({
          pathname: "/providerCard",
          state: { service, pageType },
        });
      } else {
        history.goBack();
      }
    } catch {
      setApplying(false);
    }
  };

  const segmentBase = {
    flex: 1,
    padding: "12px 0",
    borderRadius: 999,
    fontSize: 15,
    fontWeight: 700,
    textAlign: "center",
    cursor: "pointer",
    fontFamily: "'Montserrat', sans-serif",
    border: "none",
  };

  return (
    <IonPage>
      <IonContent>
        <div
          style={{
            fontFamily: "'Montserrat', sans-serif",
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            paddingBottom: 96,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "16px",
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <button
              type="button"
              onClick={() => history.goBack()}
              aria-label="Back"
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#97144D",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ArrowLeft size={22} />
            </button>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#12194C",
              }}
            >
              Filters
            </h2>
          </div>

          <div style={{ padding: "16px" }}>
            <p
              style={{
                margin: "0 0 10px 2px",
                fontSize: 15,
                fontWeight: 700,
                color: "#12194C",
              }}
            >
              Filter by location
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                padding: 5,
                border: "1px dashed #C7CBD9",
                borderRadius: 999,
              }}
            >
              <button
                type="button"
                onClick={() => setMode("maps")}
                style={{
                  ...segmentBase,
                  backgroundColor: mode === "maps" ? "#97144D" : "transparent",
                  color: mode === "maps" ? "#FFFFFF" : "#12194C",
                }}
              >
                Maps
              </button>
              <button
                type="button"
                onClick={() => setMode("manual")}
                style={{
                  ...segmentBase,
                  backgroundColor:
                    mode === "manual" ? "#97144D" : "transparent",
                  color: mode === "manual" ? "#FFFFFF" : "#12194C",
                }}
              >
                Manual
              </button>
            </div>
          </div>

          {mode === "maps" ? (
            <div
              style={{
                margin: "0 16px",
                padding: 16,
                border: "1px solid #E5E7EB",
                borderRadius: 12,
                backgroundColor: "#FAFAFB",
              }}
            >
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#12194C",
                }}
              >
                Filter by Google Maps
              </p>
              {showMapSearchPrompt ? (
                <button
                  type="button"
                  onClick={goToMapSearch}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 16px",
                    backgroundColor: "#FBE7ED",
                    border: "none",
                    borderRadius: 10,
                    cursor: "pointer",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#97144D",
                    }}
                  >
                    Search location on map
                  </span>
                  <Navigation size={18} style={{ color: "#97144D" }} />
                </button>
              ) : (
                <>
              <div
                style={{
                  position: "relative",
                  height: 200,
                  borderRadius: 10,
                  overflow: "hidden",
                  border: "1px solid #E5E7EB",
                }}
              >
                {apiKey && pickedLocation && (
                  <GoogleMapReact
                    bootstrapURLKeys={{ key: apiKey }}
                    center={{ lat: pickedLocation.lat, lng: pickedLocation.lng }}
                    zoom={14}
                    options={{
                      fullscreenControl: false,
                      mapTypeControl: false,
                      streetViewControl: false,
                      zoomControl: false,
                      gestureHandling: "none",
                      clickableIcons: false,
                    }}
                  >
                    <Pin lat={pickedLocation.lat} lng={pickedLocation.lng} />
                  </GoogleMapReact>
                )}
              </div>

              {pickedLocation?.address && (
                <p
                  style={{
                    margin: "12px 0 0",
                    fontSize: 13,
                    color: "#374151",
                    lineHeight: 1.45,
                    wordBreak: "break-word",
                  }}
                >
                  {pickedLocation.address}
                </p>
              )}

              <p
                style={{
                  margin: "18px 0 10px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#12194C",
                }}
              >
                Filter by radius
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                {RADIUS_PRESETS_KM.map((km) => {
                  const active = selectedRadiusKm === km;
                  return (
                    <button
                      key={km}
                      type="button"
                      onClick={() => handleSelectRadius(km)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        border: "1px solid #97144D",
                        color: active ? "#FFFFFF" : "#97144D",
                        backgroundColor: active ? "#97144D" : "#FFFFFF",
                      }}
                    >
                      {km} km
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={openCustomRange}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: "1px solid #97144D",
                    color: isCustomActive ? "#FFFFFF" : "#97144D",
                    backgroundColor: isCustomActive ? "#97144D" : "#FFFFFF",
                  }}
                >
                  {isCustomActive ? `Custom: ${selectedRadiusKm} km` : "Custom range"}
                </button>
              </div>
                </>
              )}
            </div>
          ) : (
            <div
              style={{
                margin: "0 16px",
                padding: 16,
                border: "1px solid #E5E7EB",
                borderRadius: 12,
                backgroundColor: "#FAFAFB",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#12194C",
                }}
              >
                Filter by location manually
              </p>

              <TextField
                select
                required
                label="State"
                size="small"
                fullWidth
                sx={FIELD_SX}
                value={manualState}
                onChange={(e) => handleStateChange(e.target.value)}
                disabled={loadingStates}
              >
                {states.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                required
                label="City"
                size="small"
                fullWidth
                sx={FIELD_SX}
                value={manualCity}
                onChange={(e) => handleCityChange(e.target.value)}
                disabled={!manualState || loadingCities}
              >
                {cities.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Locality"
                size="small"
                fullWidth
                sx={FIELD_SX}
                value={manualLocality}
                onChange={(e) => setManualLocality(e.target.value)}
                disabled={!manualCity || loadingLocalities}
              >
                {localities.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          )}

          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              padding: 16,
              backgroundColor: "#FFFFFF",
              borderTop: "1px solid #F1F5F9",
            }}
          >
            <button
              type="button"
              onClick={handleApply}
              disabled={!canApply}
              style={{
                width: "100%",
                padding: "14px 16px",
                backgroundColor: canApply ? "#97144D" : "#D1D5DB",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 700,
                cursor: canApply ? "pointer" : "not-allowed",
                fontFamily: "'Montserrat', sans-serif",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              {applying && (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{ color: "#FFFFFF" }}
                />
              )}
              {applying ? "Applying…" : "Apply filter"}
            </button>
          </div>

          {customRangeOpen && (
            <>
              <div
                className="navigo-sheet-backdrop"
                onClick={() => setCustomRangeOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  backgroundColor: "rgba(0,0,0,0.45)",
                  zIndex: 1000,
                }}
              />
              <div
                className="navigo-sheet-slide-up"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "#FFFFFF",
                  padding: "24px 20px 20px",
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                  zIndex: 1001,
                  fontFamily: "'Montserrat', sans-serif",
                  boxShadow: "0 -6px 20px rgba(0,0,0,0.12)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      backgroundColor: "#FBE7ED",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MapPin size={22} style={{ color: "#97144D" }} />
                  </div>
                  <h3
                    style={{
                      margin: "18px 0 0",
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#12194C",
                    }}
                  >
                    Choose custom range
                  </h3>
                  <p
                    style={{
                      margin: "10px 0 0",
                      fontSize: 13,
                      color: "#6B7280",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Higher the km, higher the chance of getting more service
                    providers
                  </p>
                </div>

                <div style={{ position: "relative", margin: "32px 8px 28px" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 8,
                      right: 8,
                      height: 2,
                      backgroundColor: "#E5E7EB",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      position: "relative",
                    }}
                  >
                    {CUSTOM_RANGE_STOPS_KM.map((km) => {
                      const isSelected = pendingCustomKm === km;
                      return (
                        <div
                          key={km}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setPendingCustomKm(km)}
                            aria-label={`${km} km`}
                            style={{
                              width: isSelected ? 22 : 12,
                              height: isSelected ? 22 : 12,
                              borderRadius: "50%",
                              backgroundColor: "#97144D",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              marginTop: isSelected ? 0 : 5,
                              marginBottom: isSelected ? 0 : 5,
                              boxShadow: isSelected
                                ? "0 0 0 4px rgba(151,20,77,0.18)"
                                : "none",
                            }}
                          />
                          <span
                            style={{
                              marginTop: 12,
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#12194C",
                            }}
                          >
                            {km}
                          </span>
                          <span style={{ fontSize: 11, color: "#6B7280" }}>
                            km
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={applyCustomRange}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    backgroundColor: "#97144D",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Continue
                </button>
              </div>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
